/**
 * Registro de fotos de produto.
 *
 * PARA CADASTRAR UMA FOTO:
 *   1. Coloque o arquivo em `src/assets/products/`
 *   2. No produto, escreva o nome do arquivo:  image: 'golden-adultos-15kg.jpg'
 *   3. Pronto. O Vite processa, otimiza e versiona a imagem no build.
 *
 * Também são aceitos:
 *   • URL absoluta          → 'https://cdn.exemplo.com/foto.jpg'
 *   • arquivo em `public/`  → '/produtos/foto.jpg'
 *
 * Sem isso, um caminho relativo escrito à mão no arquivo de dados vira um 404
 * silencioso: a imagem falha, o site cai na embalagem desenhada e ninguém
 * descobre que a foto foi cadastrada errado.
 *
 * FORMATO RECOMENDADO: quadrado (1:1), mínimo 800×800, fundo claro ou
 * transparente, WebP ou JPEG com até ~200 KB.
 */

/**
 * Importa tudo de `src/assets/products/` em tempo de build.
 * `eager: true` resolve as URLs de forma síncrona, então o lookup abaixo é
 * um simples acesso a objeto — sem promessa, sem estado de carregamento.
 */
const modules = import.meta.glob<string>('../assets/products/*.{png,jpg,jpeg,webp,avif,svg}', {
  eager: true,
  query: '?url',
  import: 'default',
});

/** `{ 'golden-adultos.jpg': '/assets/golden-adultos-a1b2c3.jpg' }` */
const BY_FILENAME: Record<string, string> = {};

for (const [path, url] of Object.entries(modules)) {
  const filename = path.split('/').pop();
  if (filename) BY_FILENAME[filename.toLowerCase()] = url;
}

function isExternalOrPublic(reference: string): boolean {
  return (
    reference.startsWith('http://') ||
    reference.startsWith('https://') ||
    reference.startsWith('//') ||
    reference.startsWith('/') ||
    reference.startsWith('data:')
  );
}

/**
 * Converte a referência escrita no catálogo na URL final da imagem.
 * Retorna `undefined` quando não há foto — aí o site desenha a embalagem.
 */
export function resolveProductImage(reference: string | undefined): string | undefined {
  if (!reference) return undefined;

  const trimmed = reference.trim();
  if (trimmed === '') return undefined;

  if (isExternalOrPublic(trimmed)) return trimmed;

  const resolved = BY_FILENAME[trimmed.toLowerCase()];

  if (!resolved && import.meta.env.DEV) {
    console.warn(
      `[BrucePets] Foto "${reference}" não encontrada em src/assets/products/.\n` +
        `Arquivos disponíveis: ${Object.keys(BY_FILENAME).join(', ') || '(nenhum)'}\n` +
        `Use o nome exato do arquivo, uma URL absoluta, ou um caminho a partir de public/.`,
    );
  }

  return resolved;
}

/** Nomes de arquivo disponíveis — usado pela validação do catálogo. */
export function listAvailableProductImages(): string[] {
  return Object.keys(BY_FILENAME);
}
